import { formatCount } from "@/lib/format";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link, useInRouterContext } from "react-router-dom";

import type { SectionEnvelope } from "@/api/diagnostic";
import { cn } from "@/lib/utils";
import { DiagnosticSection, Section, type DiagnosticTabProps } from "../../DiagnosticSection";
import { CardNotes } from "../../primitives/CardNotes";
import {
  DECISION_ORDER,
  UNDECIDED,
  wricefDecisions,
  type WricefDecisionFrame,
} from "../../data/wricefDecisions";
import { extensionTargets, wricefLetterColumns } from "../../diagnosticData";
import {
  makeDecisionMeta,
  makeDispositionMeta,
  makeExtensionTargetMeta,
  makeWricefTypeMeta,
  type DispositionToken,
  type ExtensionTargetToken,
  type TokenMeta,
} from "../../diagnosticMeta";
import { StackedBar } from "../../primitives/StackedBar";

/* -------------------------------------------------------------------------- */
/* 2 · catálogo WRICEF                                                         */
/* -------------------------------------------------------------------------- */

/**
 * Cores por disposição. ⚠️ O vocabulário real tem **quatro** tokens; não existe
 * "migrar como está" aqui. O token de "sem alteração" é `conversion_tipo`, outra
 * coluna, que não trafega no payload.
 */
const DISPOSITION_BAR: Record<DispositionToken, string> = {
  undecided: "bg-[#ffc83d]",
  requires_extension: "bg-[#f27325]",
  retire: "bg-[#b2bccb]",
  standard_grow_equivalent: "bg-[#27a155]",
};

/**
 * Cores por alvo de extensibilidade: **um** matiz, do claro ao escuro.
 *
 * A primeira tentativa reusou a rampa do `BUCKET_BAR` (verde → azul → ouro →
 * laranja), e a tela mostrou o erro na hora: esta barra fica no mesmo card da
 * barra de disposição, com as duas legendas uma embaixo da outra, e ali o
 * laranja já quer dizer "Requer extensão" e o ouro "Sem decisão". A mesma cor
 * passava a significar duas coisas a um centímetro de distância.
 *
 * Matiz único resolve porque diz a verdade sobre o dado: a barra inteira **é**
 * o balde `requires_extension`, subdividido por quão limpa é a técnica. Mais
 * escuro, menos limpo. O cinza do "sem alvo" não colide com o cinza de
 * `retire` na legenda de cima: nos dois o sentido é "não há trabalho de
 * extensão decidido aqui".
 *
 * O modo decisão (F5) reaproveita a mesma rampa no bloco "em escopo": ali ela
 * subdivide o azul de `in_scope`, em vez do laranja de `requires_extension`.
 */
const TARGET_BAR: Record<string, string> = {
  key_user: "bg-[#6d8fc4]",
  side_by_side: "bg-[#3f6296]",
  developer: "bg-[#24456f]",
  classic: "bg-[#08254c]",
  _UNKNOWN: "bg-[#b2bccb]",
};

/**
 * Cores por decisão, as da EF-ARCH-001 §4 e do mockup (Tela C): em escopo
 * azul, pendente ouro, fora cinza, standard verde e sem decisão cinza claro.
 * A seção "Decisão" do detalhe do objeto (F2) usa as mesmas no quadrado de
 * cada opção, para o arquiteto ver ali a cor que o card vai desenhar.
 */
/** O drill-down da lista por decisão (#1477), registrado em `details.ts`. */
const DETAIL = "wricef";

export const DECISION_BAR: Record<string, string> = {
  in_scope: "bg-[#1d4ea0]",
  client_validation: "bg-[#e0a000]",
  out_of_scope: "bg-[#9aa3af]",
  covered_by_standard: "bg-[#1f8a4c]",
  [UNDECIDED]: "bg-[#e3e7ee]",
};

export function WricefCard({ sections, snapshots, validation }: DiagnosticTabProps) {
  const { t } = useTranslation();
  // Por volume, e não na ordem do acrônimo: quem apresenta quer ver primeiro
  // onde o volume está.
  /*
    Os dois baldes que **não são letra** saem das barras (#682).

    `_NOT_APPLICABLE` é o Guia RICEFW dizendo que aquele tipo ABAP não é
    entregável por si: classe, grupo de função e include são *parte* de um
    entregável. `_UNKNOWN` é o pipeline não ter decidido. Como barra ao lado de
    Relatório e Enhancement eles se leem como tipo de entregável, e no appliance
    o primeiro era a segunda maior de todas. O desenho tem as seis letras e
    mais nada.

    Sumir com eles seria o outro erro, porque são 2.849 de 7.288 objetos ali: o
    número vai para a nota do canto, no mesmo padrão da vitalidade.
  */
  const all = [...wricefLetterColumns(sections)].sort((a, b) => b.total - a.total);
  const nonDeliverable = all.filter((column) => column.letter.startsWith("_"));
  const typeMeta = makeWricefTypeMeta(t);
  // A nota do canto fala do catálogo inteiro, nos dois modos: ela não é um
  // número da decisão, é "estas linhas nem entram na conta".
  const notes = nonDeliverable.map((column) =>
    t("{{count}} objects: {{label}}. They are outside this breakdown.", {
      count: formatCount(column.total),
      label: typeMeta[column.letter]?.label ?? column.letter,
    }),
  );

  // #1343, F5: com decisão registrada, o card é o da EF — todo número vem do
  // domínio `wricef`, nunca de `sections` (que pode estar congelado pela
  // Calibragem). Sem nenhuma decisão, `wricefDecisions` devolve `undefined` e
  // o card continua na classificação automática (D5).
  const frame = wricefDecisions(validation);
  if (frame) {
    return (
      <WricefDecisionCard
        frame={frame}
        envelope={validation?.wricef as SectionEnvelope}
        snapshots={snapshots}
        typeMeta={typeMeta}
        notes={notes}
      />
    );
  }

  // #687: para onde vai o código que exige extensão. Sai do cruzamento
  // `by_disposition_target`, nunca da marginal `by_extensibility_target`, que
  // agrega o catálogo inteiro e misturaria objeto que não é de extensão.
  const columns = all.filter((column) => !column.letter.startsWith("_"));
  const meta = makeDispositionMeta(t);
  const targets = extensionTargets(sections);
  const targetMeta = makeExtensionTargetMeta(t);
  // O subtítulo do desenho diz de quantos objetos o card fala. A soma das
  // colunas é o próprio catálogo desenhado, e não um total de outra origem que
  // pudesse divergir das barras logo abaixo.
  const catalogTotal = columns.reduce((acc, column) => acc + column.total, 0);

  return (
    <Section
      sections={sections}
      name="wricef_catalog"
      title={t("Custom code by type (WRICEF)")}
      subtitle={
        catalogTotal > 0
          ? t("The {{count}} Z/Y objects, classified automatically", {
              count: formatCount(catalogTotal),
            })
          : undefined
      }
      actions={<CardNotes testId="wricef-notes" notes={notes} />}
      snapshots={snapshots}
    >
      {/*
        Barra horizontal com o nome por extenso, e não coluna vertical rotulada
        pela letra do acrônimo. Quem está do outro lado da mesa não lê WRICEF;
        lê "Relatórios" e "Formulários". A ordem é por volume, porque o acrônimo
        é mnemônico e não hierarquia, e quem apresenta quer ver primeiro onde o
        volume está.

        O empilhamento por disposição fica: é informação que a barra lisa do
        desenho não tem, e a legenda logo abaixo já a explica. O que muda é a
        orientação e o rótulo, que era o que tornava o card ilegível.
      */}
      <div data-testid="wricef-letters" className="flex flex-col gap-2">
        {columns.map((column) => (
          <div
            key={column.letter}
            data-testid={`wricef-row-${column.letter}`}
            className="flex items-center gap-3"
          >
            <div className="w-32 shrink-0 truncate text-[12px] text-[#08254c]">
              {typeMeta[column.letter]?.label ?? column.letter}
            </div>
            <div className="min-w-0 flex-1">
              <StackedBar
                height="h-5"
                segments={column.segments.map((segment) => ({
                  key: segment.disposition,
                  label: meta[segment.disposition as DispositionToken]?.label ?? segment.disposition,
                  className: DISPOSITION_BAR[segment.disposition as DispositionToken] ?? "bg-[#e8ebf0]",
                  value: segment.value,
                }))}
              />
            </div>
            <div className="w-16 shrink-0 text-right text-[12px] font-semibold tabular-nums text-[#08254c]">
              {formatCount(column.total)}
            </div>
          </div>
        ))}
      </div>

      <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1">
        {(Object.keys(DISPOSITION_BAR) as DispositionToken[]).map((token) => (
          <li key={token} className="flex items-center gap-1.5 text-[11px] text-[#596b85]">
            <span className={cn("h-2.5 w-2.5 rounded-sm", DISPOSITION_BAR[token])} />
            {meta[token].label}
          </li>
        ))}
      </ul>

      {targets && (
        <div data-testid="extension-target-breakdown" className="mt-6 border-t border-[#e8ebf0] pt-4">
          <div className="flex items-baseline justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#7b8799]">
              {t("Where the extension work goes")}
            </div>
            <div className="font-mono text-[11px] tabular-nums text-[#7b8799]">
              {t("{{count}} objects requiring extension", { count: targets.total })}
            </div>
          </div>

          {/*
            Mesma correção da barra de vitalidade: o key user com 21 objetos
            ficava com uma fatia fina demais para o número caber, e a legenda
            manual daqui só trazia o nome. A legenda do próprio `StackedBar`
            carrega o valor.
          */}
          <StackedBar
            className="mt-2"
            height="h-7"
            legend
            segments={targets.slices.map((slice) => ({
              key: slice.target,
              label: targetMeta[slice.target as ExtensionTargetToken]?.label ?? slice.target,
              className: TARGET_BAR[slice.target] ?? "bg-[#e8ebf0]",
              value: slice.value,
            }))}
            renderSegment={(segment) => (
              <span className="px-1 font-mono text-[10px] font-semibold text-white">
                {formatCount(segment.value)}
              </span>
            )}
          />

          {targets.outsideExtension > 0 && (
            <p
              data-testid="extension-target-outside"
              className="mt-3 text-[11px] leading-snug text-[#7b8799]"
            >
              {t(
                "{{count}} more objects carry an extensibility target without being classified as requiring extension. They are not counted above.",
                { count: targets.outsideExtension },
              )}
            </p>
          )}
        </div>
      )}
    </Section>
  );
}

/**
 * O card da EF-ARCH-001 (#1343, F5): mesmo frame visual do catálogo, mas toda
 * barra, legenda e o bloco de baixo saem do domínio `wricef`, nunca de
 * `sections`. O envelope do domínio decide estado, rodapé de origem e corpo
 * fechado — por isso `DiagnosticSection` direto, e não o `Section` que lê
 * `wricef_catalog`.
 */
function WricefDecisionCard({
  frame,
  envelope,
  snapshots,
  typeMeta,
  notes,
}: {
  frame: WricefDecisionFrame;
  envelope: SectionEnvelope;
  snapshots?: DiagnosticTabProps["snapshots"];
  typeMeta: Record<string, TokenMeta>;
  notes: string[];
}) {
  const { t } = useTranslation();
  // O card também é desenhado fora de um roteador (testes do ato e exportação):
  // ali não há para onde o link levar.
  const inRouter = useInRouterContext();
  const meta = makeDecisionMeta(t);
  const targetMeta = makeExtensionTargetMeta(t);
  const columns = [...frame.columns].sort((a, b) => b.total - a.total);
  // `frame.byDecision` não tem `_UNDECIDED` — ele vem de `frame.undecided`, à
  // parte, porque é `universe - decided`, não uma das quatro decisões.
  const legendCounts: Record<string, number> = { ...frame.byDecision, [UNDECIDED]: frame.undecided };

  return (
    <DiagnosticSection
      envelope={envelope}
      title={t("Custom code by type (WRICEF)")}
      subtitle={t(
        "{{total}} Z/Y objects · technical architect's decision on {{decided}} of {{total}}",
        { total: formatCount(frame.universe), decided: formatCount(frame.decided) },
      )}
      actions={
        <div className="flex items-center gap-3">
          {inRouter && (
            <Link
              to={DETAIL}
              data-testid="wricef-see-items"
              className="inline-flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#08254c] hover:underline"
            >
              {t("See items")}
              <ArrowRight aria-hidden className="h-3 w-3" />
            </Link>
          )}
          <CardNotes testId="wricef-notes" notes={notes} />
        </div>
      }
      snapshots={snapshots}
    >
      <div data-testid="wricef-letters" className="flex flex-col gap-2">
        {columns.map((column) => (
          <div
            key={column.letter}
            data-testid={`wricef-row-${column.letter}`}
            className="flex items-center gap-3"
          >
            <div className="w-32 shrink-0 truncate text-[12px] text-[#08254c]">
              {typeMeta[column.letter]?.label ?? column.letter}
            </div>
            <div className="min-w-0 flex-1">
              <StackedBar
                height="h-5"
                segments={column.segments.map((segment) => ({
                  key: segment.decision,
                  label: meta[segment.decision]?.label ?? segment.decision,
                  className: DECISION_BAR[segment.decision] ?? "bg-[#e8ebf0]",
                  value: segment.value,
                }))}
              />
            </div>
            <div className="w-16 shrink-0 text-right text-[12px] font-semibold tabular-nums text-[#08254c]">
              {formatCount(column.total)}
            </div>
          </div>
        ))}
      </div>

      {/*
        Cada item da legenda abre a lista já filtrada por aquela decisão (#1477):
        é por aqui que, na reunião, quem apresenta chega aos pendentes.
      */}
      <ul className="mt-4 flex flex-wrap gap-x-4 gap-y-1">
        {DECISION_ORDER.map((token) => {
          const label = (
            <>
              <span className={cn("h-2.5 w-2.5 rounded-sm", DECISION_BAR[token])} />
              {meta[token].label} {formatCount(legendCounts[token] ?? 0)}
            </>
          );
          return (
            <li key={token} className="flex items-center gap-1.5 text-[11px] text-[#596b85]">
              {inRouter ? (
                <Link
                  to={`${DETAIL}?decision=${token}`}
                  data-testid={`wricef-legend-${token}`}
                  className="flex items-center gap-1.5 hover:text-[#08254c] hover:underline"
                >
                  {label}
                </Link>
              ) : (
                label
              )}
            </li>
          );
        })}
      </ul>

      {frame.inScope && (
        <div data-testid="wricef-in-scope-breakdown" className="mt-6 border-t border-[#e8ebf0] pt-4">
          <div className="flex items-baseline justify-between">
            <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-[#7b8799]">
              {t("Where the conversion work goes")}
            </div>
            <div className="font-mono text-[11px] tabular-nums text-[#7b8799]">
              {t("{{count}} objects in scope · {{hours}} h", {
                count: formatCount(frame.inScope.count),
                hours: formatCount(frame.inScope.hours),
              })}
            </div>
          </div>

          <StackedBar
            className="mt-2"
            height="h-7"
            legend
            segments={frame.inScope.slices.map((slice) => ({
              key: slice.strategy,
              label: targetMeta[slice.strategy as ExtensionTargetToken]?.label ?? slice.strategy,
              className: TARGET_BAR[slice.strategy] ?? "bg-[#e8ebf0]",
              value: slice.count,
            }))}
            renderSegment={(segment) => (
              <span className="px-1 font-mono text-[10px] font-semibold text-white">
                {formatCount(segment.value)}
              </span>
            )}
          />

          {frame.inScope.missingHours > 0 && (
            <p
              data-testid="wricef-in-scope-missing-hours"
              className="mt-3 text-[11px] leading-snug text-[#7b8799]"
            >
              {t("{{count}} in-scope objects have no hours yet. They are not in the total.", {
                count: formatCount(frame.inScope.missingHours),
              })}
            </p>
          )}
        </div>
      )}
    </DiagnosticSection>
  );
}
